import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import api from '../services/api';

// AI trade messages
const AI_MESSAGES = [
    'Quantum analysis complete',
    'Pattern recognition: 98.7%',
    'Market sentiment: BULLISH',
    'Deep learning active',
    'Signal amplification ready',
    'Risk matrix optimized',
    'Predictive model loaded',
    'Alpha generation online'
];

// Generate AI-style trade
const generateAITrade = () => {
    const coins = ['BTC', 'ETH', 'SOL', 'XRP', 'BNB', 'ADA', 'AVAX', 'MATIC'];
    const types = ['BUY', 'SELL'];
    const strategies = ['MOMENTUM', 'ARBITRAGE', 'SCALP', 'SWING', 'BREAKOUT'];
    const coin = coins[Math.floor(Math.random() * coins.length)];
    const type = types[Math.floor(Math.random() * types.length)];
    const strategy = strategies[Math.floor(Math.random() * strategies.length)];
    const amount = (Math.random() * 5 + 0.1).toFixed(4);
    const confidence = (88 + Math.random() * 11).toFixed(1);
    const price = coin === 'BTC' ? (87000 + Math.random() * 4000).toFixed(2) :
                  coin === 'ETH' ? (2900 + Math.random() * 300).toFixed(2) :
                  coin === 'SOL' ? (120 + Math.random() * 30).toFixed(2) :
                  (Math.random() * 200 + 1).toFixed(2);
    
    return {
        id: Date.now() + Math.random(),
        coin, type, amount, price, confidence, strategy,
        timestamp: new Date().toLocaleTimeString('en-US', { hour12: false }),
        isBuy: type === 'BUY'
    };
};

const LiveTradingContext = createContext(null);

export const LiveTradingProvider = ({ children }) => {
    const [trades, setTrades] = useState([]);
    const [aiStatus, setAiStatus] = useState('Initializing quantum core...');
    const [stats, setStats] = useState({ total: 0, buys: 0, sells: 0, accuracy: 94.7 });
    const [systemMetrics, setSystemMetrics] = useState({ cpu: 67, mem: 45, net: 89 });
    const [isInitialized, setIsInitialized] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    
    const isInitializedRef = useRef(false);
    const intervalRefs = useRef({
        status: null,
        metrics: null,
        trade: null
    });
    const saveTimeoutRef = useRef(null);

    // Load persisted state from backend on mount
    const loadPersistedState = useCallback(async () => {
        try {
            const response = await api.get('/trading-bot/state');
            if (response.data) {
                setStats({
                    total: response.data.total_trades || 0,
                    buys: response.data.buys || 0,
                    sells: response.data.sells || 0,
                    accuracy: response.data.accuracy || 94.7
                });
            }
        } catch (error) {
            console.log('No persisted trading bot state found, starting fresh');
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Save state to backend (debounced)
    const saveState = useCallback(async (newStats) => {
        // Clear any pending save
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        
        // Debounce save to reduce API calls
        saveTimeoutRef.current = setTimeout(async () => {
            try {
                await api.put('/trading-bot/state', {
                    total_trades: newStats.total,
                    buys: newStats.buys,
                    sells: newStats.sells,
                    accuracy: newStats.accuracy
                });
            } catch (error) {
                console.error('Failed to save trading bot state:', error);
            }
        }, 2000); // Save every 2 seconds max
    }, []);

    // Record single trade to backend
    const recordTrade = useCallback(async (isBuy) => {
        try {
            const response = await api.post(`/trading-bot/trade?is_buy=${isBuy}`);
            if (response.data) {
                setStats({
                    total: response.data.total_trades,
                    buys: response.data.buys,
                    sells: response.data.sells,
                    accuracy: response.data.accuracy
                });
            }
        } catch (error) {
            console.error('Failed to record trade:', error);
        }
    }, []);

    const startTrading = useCallback(() => {
        // Use ref to prevent duplicate initialization even if callback recreates
        if (isInitializedRef.current) return;
        isInitializedRef.current = true;
        setIsInitialized(true);
        
        // Status rotation
        let statusIndex = 0;
        intervalRefs.current.status = setInterval(() => {
            setAiStatus(AI_MESSAGES[statusIndex % AI_MESSAGES.length]);
            statusIndex++;
        }, 2500);

        // System metrics update
        intervalRefs.current.metrics = setInterval(() => {
            setSystemMetrics({
                cpu: 60 + Math.random() * 35,
                mem: 40 + Math.random() * 30,
                net: 80 + Math.random() * 19
            });
        }, 1500);

        // Trade generation
        const generateNewTrade = () => {
            const trade = generateAITrade();
            setTrades(prev => [...prev.slice(-2), trade]);
            
            // Update local state immediately for UI
            setStats(prev => {
                const newStats = {
                    total: prev.total + 1,
                    buys: prev.buys + (trade.isBuy ? 1 : 0),
                    sells: prev.sells + (!trade.isBuy ? 1 : 0),
                    accuracy: 92 + Math.random() * 7
                };
                // Save to backend
                recordTrade(trade.isBuy);
                return newStats;
            });
        };

        const scheduleNextTrade = () => {
            const delay = 2500 + Math.random() * 3000;
            intervalRefs.current.trade = setTimeout(() => {
                generateNewTrade();
                scheduleNextTrade();
            }, delay);
        };

        setTimeout(generateNewTrade, 800);
        scheduleNextTrade();
    }, [recordTrade]);

    const removeTrade = useCallback((id) => {
        setTrades(prev => prev.filter(t => t.id !== id));
    }, []);

    // Load persisted state when provider mounts
    useEffect(() => {
        loadPersistedState();
        
        // Cleanup on unmount
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, [loadPersistedState]);

    const value = {
        trades,
        aiStatus,
        stats,
        systemMetrics,
        isInitialized,
        isLoading,
        startTrading,
        removeTrade
    };

    return (
        <LiveTradingContext.Provider value={value}>
            {children}
        </LiveTradingContext.Provider>
    );
};

export const useLiveTrading = () => {
    const context = useContext(LiveTradingContext);
    if (!context) {
        throw new Error('useLiveTrading must be used within a LiveTradingProvider');
    }
    return context;
};

export default LiveTradingContext;
